import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { Button } from '@/components/shadcn/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form'
import { Switch } from '@/components/shadcn/switch'
import { Textarea } from '@/components/shadcn/textarea'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconArrowUpOutline } from '@icons/IconArrowUpOutline'
import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

type MessagesCollaborationCollaborationLog = RestSchemas['Messages.Collaboration.CollaborationLog']

const formSchema = z.object({
  resolve: z.boolean(),
  reply: z
    .string()
    .min(2, 'Reply is required and must be at least 2 characters')
    .max(500, 'Reply cannot exceed 500 characters'),
  assignee: z.number().optional(),
})

export function CreateLogEntryForm({
  lastLogEntry: _lastLogEntry,
  channelId,
  threadId,
  assignee: initialAssignee,
  className,
  invalidateThreadAndLogs,
}: {
  lastLogEntry: MessagesCollaborationCollaborationLog | null
  channelId?: number
  threadId?: number
  assignee?: number | null
  className?: string
  invalidateThreadAndLogs: () => void
}) {
  const { appId, workspaceId } = useAppContext()

  const {
    getCollaborationApiGetThreadWithLogs,
    putCollaborationApiAssignCollaborationThread,
    putCollaborationApiCommentCollaborationThread,
    putCollaborationApiResolveCollaborationThread,
  } = useRestClient()

  const { data: threadWithLogs } = getCollaborationApiGetThreadWithLogs.useSuspenseQuery({
    path: { threadId: threadId! },
  })

  const { users = [] } = threadWithLogs

  const { mutate: assignThread } = putCollaborationApiAssignCollaborationThread.useMutation()
  const { mutate: commentThread } = putCollaborationApiCommentCollaborationThread.useMutation()
  const { mutate: resolveThread } = putCollaborationApiResolveCollaborationThread.useMutation()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      resolve: false,
      reply: '',
      assignee: undefined,
    },
  })

  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  const onSuccess = () => {
    toast.success('Log entry created successfully.')
    invalidateThreadAndLogs()
    form.reset()
  }

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    const { resolve, assignee, reply } = data
    if (!appId || !workspaceId || !channelId || !threadId) {
      toast.error('App ID, workspace ID, and channel ID are required to create a new log entry.')
      return
    }

    if (resolve) {
      resolveThread(
        {
          path: { applicationId: appId, workspaceId, channelId, threadId },
          body: { comment: reply },
        },
        {
          onSuccess: onSuccess,
          onError: () => {
            toast.error('Failed to resolve log entry.')
          },
        }
      )
      return
    }

    if (assignee && assignee !== initialAssignee) {
      assignThread(
        {
          path: { applicationId: appId, workspaceId, channelId, threadId },
          body: { assigneeUserId: assignee, comment: reply },
        },
        {
          onSuccess: onSuccess,
          onError: () => {
            toast.error('Failed to assign user to the log entry.')
          },
        }
      )
      return
    }

    commentThread(
      {
        path: { applicationId: appId, workspaceId, channelId, threadId },
        body: { comment: reply },
      },
      {
        onSuccess: onSuccess,
        onError: () => {
          toast.error('Failed to comment on the log entry.')
        },
      }
    )
  }

  const watchResolve = useWatch({ control: form.control, name: 'resolve' })

  useEffect(() => {
    if (watchResolve) {
      form.setValue('assignee', undefined)
    }
  }, [form, watchResolve])

  if (channelId === undefined) {
    return null
  }

  return (
    <Form {...form}>
      <form className={cn('w-full space-y-2', className)} onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="reply"
          render={({ field }) => (
            <div className="relative">
              <FormItem>
                <FormLabel className="sr-only">Reply</FormLabel>
                <FormDescription className="sr-only text-xs">Post your reply.</FormDescription>
                <FormControl>
                  <Textarea
                    {...field}
                    ref={(el) => {
                      field.ref(el)
                      replyTextareaRef.current = el
                    }}
                    placeholder="Post your reply..."
                    autoFocus
                    className="min-h-[40px] rounded-l-3xl rounded-r-3xl pr-11"
                    rows={1}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
              <Button type="submit" size="icon" className="absolute bottom-0.5 right-1 size-9 !rounded-full">
                <IconArrowUpOutline />
                <span className="sr-only">Submit</span>
              </Button>
            </div>
          )}
        />
        <div className="flex w-full items-center justify-between gap-4 pl-5">
          <FormField
            control={form.control}
            name="assignee"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1 space-y-0">
                <div className="flex min-w-0 items-center gap-2">
                  <FormLabel className="shrink-0 whitespace-nowrap">Assign to:</FormLabel>
                  <FormControl className="min-w-0 flex-1">
                    <SingleSelectCombobox
                      options={
                        (users
                          ?.filter((user) => user?.id != null)
                          .map((user) => ({
                            value: user.id as number,
                            label: clsx(user.givenName, user.familyName, ` - ( ${user.email} )`),
                          })) || []) as { value: number; label: string }[]
                      }
                      selectedValue={field.value || null}
                      onSelectionChange={(selectedUserId) => {
                        field.onChange(selectedUserId)

                        if (selectedUserId == null) return

                        const currentReply = (form.getValues('reply') || '').trim()
                        if (!currentReply) {
                          const selectedUser = users?.find((u) => u.id === selectedUserId)
                          if (selectedUser) {
                            const fullName = `${selectedUser.givenName ?? ''} ${selectedUser.familyName ?? ''}`.trim()
                            const defaultReply = `Re-assigned to ${fullName || 'selected user'}`
                            form.setValue('reply', defaultReply, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })

                            // Focus and select the populated reply for easy overwrite
                            setTimeout(() => {
                              if (replyTextareaRef.current) {
                                replyTextareaRef.current.focus()
                                replyTextareaRef.current.select()
                              }
                            }, 0)
                          }
                        }
                      }}
                      placeholder="Select a user"
                      searchPlaceholder="Search users..."
                      emptyMessage="No users found."
                      disabled={watchResolve}
                      allowClear={true}
                      className="!my-0 w-full min-w-[12rem] max-w-full"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="resolve"
            render={({ field }) => (
              <FormItem className="shrink-0 space-y-0">
                <div className="flex flex-col items-center gap-y-0.5">
                  <FormDescription className="sr-only text-xs">Resolve the log entry.</FormDescription>
                  <FormControl>
                    <Switch
                      className="h-4"
                      thumbClassName="h-3"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked)

                        if (checked) {
                          const currentReply = (form.getValues('reply') || '').trim()
                          if (!currentReply) {
                            form.setValue('reply', 'Resolving the thread.', {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                            setTimeout(() => {
                              if (replyTextareaRef.current) {
                                replyTextareaRef.current.focus()
                                replyTextareaRef.current.select()
                              }
                            }, 0)
                          }
                        }
                      }}
                    />
                  </FormControl>
                  <FormLabel className="text-xs">Resolve thread</FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          {/* <Button type="submit" size="icon" className="size-9 rounded-full">
            <IconPaperAirplaneOutline />
            <span className="sr-only">Submit</span>
          </Button> */}
        </div>
      </form>
    </Form>
  )
}
